import { useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, cn } from '@bliks-io/ui';
import type { PartnerMaterialLocale, PartnerNewsAction } from '@bliks-io/contracts';
import { pickPartnerMaterialText } from '@/lib/partner-material-text';
import { formatDate } from '@/lib/formatters';

type LocalizedText = Partial<Record<PartnerMaterialLocale, string>>;

/** What one story card shows; the admin preview fills it from an unsaved form. */
export interface PartnerNewsSlide {
  id: string;
  eyebrow?: LocalizedText;
  title: LocalizedText;
  body: LocalizedText;
  primaryAction?: PartnerNewsAction;
  secondaryAction?: PartnerNewsAction;
  footnote?: LocalizedText;
  logoUrl?: string;
  imageUrl?: string;
  publishedAt?: string;
}

interface PartnerNewsStoryProps {
  slides: PartnerNewsSlide[];
  locale: PartnerMaterialLocale;
  /** The admin preview holds one card still; partners see the story run. */
  autoAdvance?: boolean;
}

/**
 * News as a story: one card at a time, a bar per card that fills and moves on
 * by itself. The bar's CSS animation is the timer, so hovering, focusing or a
 * reduced-motion setting stop it without any timer to clear.
 */
export function PartnerNewsStory({ slides, locale, autoAdvance = true }: PartnerNewsStoryProps) {
  const { t } = useTranslation();
  const [requested, setRequested] = useState(0);
  const [paused, setPaused] = useState(false);
  // A shorter list after a refetch must not point past its end.
  const active = slides.length ? requested % slides.length : 0;
  if (slides.length === 0) return null;

  const goTo = (index: number) => setRequested((index + slides.length) % slides.length);
  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'ArrowRight') goTo(active + 1);
    if (event.key === 'ArrowLeft') goTo(active - 1);
  };

  return (
    <Card>
      <section
        aria-roledescription={t('partner.home.news.storyLabel')}
        aria-label={t('partner.home.news.title')}
        tabIndex={0}
        onKeyDown={onKeyDown}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocus={() => setPaused(true)}
        onBlur={() => setPaused(false)}
        className="flex flex-col gap-5 rounded-lg p-6 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
      >
        {/* Every card sits in the same grid cell, so the story takes the height
            of its tallest card and does not jump when one has no picture. */}
        <div className="grid">
          {slides.map((entry, index) => (
            <div
              key={entry.id}
              inert={index !== active}
              aria-hidden={index !== active}
              className={cn('col-start-1 row-start-1', index !== active && 'invisible')}
            >
              <SlideContent slide={entry} locale={locale} />
            </div>
          ))}
        </div>
        {slides.length > 1 && (
          <div className="flex gap-2">
            {slides.map((entry, index) => (
              <button
                key={entry.id}
                type="button"
                aria-label={t('partner.home.news.goTo', { index: index + 1, count: slides.length })}
                aria-current={index === active ? 'true' : undefined}
                onClick={() => goTo(index)}
                className="flex h-3 flex-1 items-center"
              >
                <span className="h-1 w-full overflow-hidden rounded-full bg-muted">
                  {index < active && <span className="block h-full w-full bg-bliks-green-500" />}
                  {index === active && (
                    <span
                      key={`${entry.id}-${active}`}
                      className={cn(
                        'block h-full w-full origin-left bg-bliks-green-500',
                        autoAdvance && 'animate-story-progress motion-reduce:animate-none'
                      )}
                      style={{ animationPlayState: paused ? 'paused' : 'running' }}
                      onAnimationEnd={() => goTo(active + 1)}
                    />
                  )}
                </span>
              </button>
            ))}
          </div>
        )}
      </section>
    </Card>
  );
}

function SlideContent({
  slide,
  locale
}: {
  slide: PartnerNewsSlide;
  locale: PartnerMaterialLocale;
}) {
  const eyebrow = pickPartnerMaterialText(slide.eyebrow, locale);
  const title = pickPartnerMaterialText(slide.title, locale);
  const body = pickPartnerMaterialText(slide.body, locale);
  const footnote = pickPartnerMaterialText(slide.footnote, locale);

  return (
    <article className="flex flex-col gap-4">
      {slide.logoUrl && (
        <img src={slide.logoUrl} alt="" className="h-8 w-auto max-w-56 self-start object-contain" />
      )}
      {eyebrow && (
        <p className="text-xs font-semibold uppercase tracking-wide text-bliks-green-700">
          {eyebrow}
        </p>
      )}
      <div className="flex gap-5">
        {slide.imageUrl && (
          <img
            src={slide.imageUrl}
            alt=""
            className="aspect-square w-36 shrink-0 rounded-lg border object-cover"
          />
        )}
        <div className="flex min-w-0 flex-col gap-2">
          <h3 className="text-xl font-semibold text-foreground">{title}</h3>
          {body && (
            <p className="line-clamp-4 whitespace-pre-line text-sm text-fg-secondary">{body}</p>
          )}
          {slide.publishedAt && (
            <p className="text-xs text-muted-foreground">{formatDate(slide.publishedAt)}</p>
          )}
        </div>
      </div>
      {(slide.primaryAction || slide.secondaryAction) && (
        <div className="flex flex-wrap gap-3">
          {slide.primaryAction && (
            <ActionButton action={slide.primaryAction} locale={locale} variant="primary" />
          )}
          {slide.secondaryAction && (
            <ActionButton action={slide.secondaryAction} locale={locale} variant="outline" />
          )}
        </div>
      )}
      {footnote && <p className="text-xs text-muted-foreground">{footnote}</p>}
    </article>
  );
}

function ActionButton({
  action,
  locale,
  variant
}: {
  action: PartnerNewsAction;
  locale: PartnerMaterialLocale;
  variant: 'primary' | 'outline';
}) {
  return (
    <Button variant={variant} asChild>
      <a href={action.url} target="_blank" rel="noopener noreferrer">
        {pickPartnerMaterialText(action.label, locale)}
      </a>
    </Button>
  );
}

/** The story slides for news as the API returns them. */
export function toNewsSlides(
  news: {
    _id: string;
    eyebrow?: LocalizedText;
    title: LocalizedText;
    body: LocalizedText;
    primaryAction?: PartnerNewsAction;
    secondaryAction?: PartnerNewsAction;
    footnote?: LocalizedText;
    logo?: { url: string };
    image?: { url: string };
    publishedAt?: string;
  }[]
): PartnerNewsSlide[] {
  return news.map(({ _id, logo, image, ...rest }) => ({
    id: _id,
    ...rest,
    logoUrl: logo?.url,
    imageUrl: image?.url
  }));
}
